import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ethers } from 'ethers'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, Calendar, Check, Clock, Gift, Loader2, QrCode, Ticket } from 'lucide-react'
import {
	attemptOpenNativeBeamioApp,
	BEAMIO_ANDROID_STORE_URL,
	BEAMIO_IOS_STORE_URL,
	buildAndroidSilentProbeMissUrl,
	consumeAndroidSilentProbeMiss,
	isAndroidDevice,
	isBeamioNativeShell,
	isIosDevice,
	isMobileDevice,
	markConsumerAppInstalledHint,
	openBeamioAppOrStore,
	resolveAndroidConsumerAppLikelyInstalled,
	shouldSkipSilentNativeAppProbe,
} from '../utils/nativeAppDownload'
import {
	buildPlayStoreUrlWithDeepLinkReferrer,
	stashPendingConsumerDeepLink,
} from '../utils/pendingConsumerDeepLink'
import {
	applyCouponClaimShareMeta,
	buildAppDownloadShareUrl,
	couponExpiryUsesUrgentVariant,
	shouldShowCouponExpiryPill,
	fetchCouponClaimShareMeta,
	type CouponClaimShareMeta,
} from '../utils/couponClaimShare'
import { fetchCouponShareSeriesExtras } from '../utils/fetchCouponShareSeriesExtras'
import { CouponTicketAddressMetaRow } from '../components/CouponTicketAddressMetaRow'
import { CatalogVideoOgBannerMedia } from '../components/CatalogVideoOgBannerMedia'
import {
	CATALOG_VIDEO_OG_APP_DOWNLOAD_BANNER_HEIGHT_PX,
	CATALOG_VIDEO_OG_BELOW_BANNER_ROW_OG_PREVIEW_CLASSNAME,
} from '../utils/catalogProductionVideoOg'
import {
	parseCouponOpenClaimFromTarget,
	parseDiscoverMerchantCardFromTarget,
	parseDiscoverMerchantOpenFromTarget,
	recordDiscoverShareClickIfNeeded,
} from '../utils/discoverShareClickEvent'
import {
	fetchCardProgramSocialSummary,
	fetchCouponProgramSocialSummary,
	type CardProgramSocialSummary,
} from '../utils/cardProgramSocialStats'
import { DiscoverMerchantShareDetail } from '../components/DiscoverMerchantShareDetail'
import AppDownloadDiscoverTopBar from '../components/AppDownloadDiscoverTopBar'
import AppDownloadMyWalletPanel from '../components/AppDownloadMyWalletPanel'
import AppDownloadPayCodeSheet from '../components/AppDownloadPayCodeSheet'
import AppDownloadPersonalInvitation, {
	parseInviteEoaFromAppTarget,
	type InvitationCtaMode,
} from '../components/AppDownloadPersonalInvitation'
import {
	loadAppDownloadVisitWalletProfile,
	prepareVisitWalletForOpenClaim,
	provisionWebShareVisitWallet,
	resolveSigningWalletFromBlob,
	type AppDownloadVisitWalletProfile,
} from '../utils/beamioWebShareWallet'
import { postCardCouponOpenClaimWithWallet, resolveCouponOpenClaimEligibilityForItem, type CardActiveIssuedCouponSeriesItem, type CouponOpenClaimEligibility } from '../utils/discoverCouponOpenClaim'
import { useScrollCapsuleOpacity } from '../hooks/useScrollCapsuleOpacity'
import { useVisitWalletAaReady } from '../hooks/useVisitWalletAaReady'
import { BEAMIO_FIXED_CAPSULE_SCROLL_TOP_SPACER } from '../utils/beamioFixedTopCapsuleLayout'

type CouponTempClaimStatus = 'idle' | 'loading' | 'success' | 'error'

type PagePhase = 'checking' | 'install' | 'desktop'

/** POS Check Balance claim button — same orange/red gradient. */
const CLAIM_GRADIENT = 'linear-gradient(to bottom right, rgb(255,132,36), rgb(255,71,87))'

function isPersonalInviteMeta(meta: CouponClaimShareMeta | null | undefined): boolean {
	return meta?.shareKind === 'personal_invite'
}

function isCouponShareMeta(meta: CouponClaimShareMeta): boolean {
	if (isPersonalInviteMeta(meta)) return false
	return meta.distributionKind !== 'catalog'
}

function resolveBeamioAppTarget(search: string): string {
	const target = new URLSearchParams(search).get('target')?.trim() ?? ''
	if (!target) return ''
	try {
		const url = new URL(target)
		if (url.origin !== 'https://beamio.app') return ''
		if (url.pathname !== '/app/' && url.pathname !== '/app' && !url.pathname.startsWith('/app/')) return ''
		return url.toString()
	} catch {
		return ''
	}
}

/** Only the native Consumer shell should skip the share landing (embedded Telegram must not auto-jump to web `/app/`). */
function shouldRedirectToInnerAppTarget(): boolean {
	return isBeamioNativeShell()
}

function CouponBannerImage({ src }: { src: string }) {
	return (
		<div className="absolute inset-0 overflow-hidden">
			<div
				className="absolute inset-y-0 left-0 w-1/2 scale-110 bg-cover bg-left bg-no-repeat blur-xl"
				style={{ backgroundImage: `url("${src}")` }}
				aria-hidden
			/>
			<div
				className="absolute inset-y-0 right-0 w-1/2 scale-110 bg-cover bg-right bg-no-repeat blur-xl"
				style={{ backgroundImage: `url("${src}")` }}
				aria-hidden
			/>
			<img
				src={src}
				alt=""
				className="absolute left-1/2 top-0 z-[1] h-full w-auto max-w-none -translate-x-1/2 object-contain"
				draggable={false}
			/>
		</div>
	)
}

function useCatalogVideoOgShareCardMaxHeight(cardRef: React.RefObject<HTMLElement | null>) {
	const [maxHeightPx, setMaxHeightPx] = useState<number | undefined>(undefined)

	useLayoutEffect(() => {
		const measure = () => {
			const el = cardRef.current
			if (!el) return
			const top = el.getBoundingClientRect().top
			const safeBottomRaw = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)')
			const safeBottom = Number.parseFloat(safeBottomRaw) || 0
			const reservePx = 20 + safeBottom
			const next = Math.floor(window.innerHeight - top - reservePx)
			setMaxHeightPx(Math.max(280, next))
		}

		measure()
		window.addEventListener('resize', measure)
		window.visualViewport?.addEventListener('resize', measure)
		window.visualViewport?.addEventListener('scroll', measure)
		return () => {
			window.removeEventListener('resize', measure)
			window.visualViewport?.removeEventListener('resize', measure)
			window.visualViewport?.removeEventListener('scroll', measure)
		}
	}, [cardRef])

	return maxHeightPx
}

function CatalogShareCategoryLine({
	globalCategory,
	itemCategory,
	tone,
}: {
	globalCategory?: string
	itemCategory?: string
	tone: 'inner' | 'external'
}) {
	const global = globalCategory?.trim() ?? ''
	const item = itemCategory?.trim() ?? ''
	if (!global && !item) return null
	const label = [global, item].filter(Boolean).join(' · ')
	const className =
		tone === 'inner'
			? 'text-[10px] font-bold uppercase tracking-wider text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]'
			: 'text-[10px] font-bold uppercase tracking-wider text-[#ea580c]'
	return <p className={className}>{label}</p>
}

function CatalogShareMetadataBlock({
	meta,
	tone,
	showExpiryPill,
	renderExpiryPill,
	descriptionViewportScroll = false,
	tokenId = null,
	supplySummary = null,
	likeCount = null,
	shareClickCount = null,
}: {
	meta: CouponClaimShareMeta
	tone: 'inner' | 'external'
	showExpiryPill: boolean
	renderExpiryPill: (placement: 'inner' | 'external') => React.ReactNode
	/** Catalog videoOg — description fills remaining card height and scrolls (no line clamp). */
	descriptionViewportScroll?: boolean
	/** Coupon NFT capsule — issued series tokenId. */
	tokenId?: string | null
	supplySummary?: string | null
	likeCount?: number | null
	shareClickCount?: number | null
}) {
	const isCatalog = meta.distributionKind === 'catalog'
	const isDiscoverMerchant =
		meta.shareKind === 'discover_merchant' || meta.distributionKind === 'merchant'
	const title = meta.title.trim()
	const subtitle = meta.subtitle.trim()
	const titleClass =
		tone === 'inner'
			? 'break-words font-manrope text-[1.05rem] font-extrabold leading-tight tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] sm:text-lg'
			: 'break-words font-manrope text-[1.05rem] font-extrabold leading-tight tracking-tight text-[#2c2f31] sm:text-lg'
	const subtitleClass =
		tone === 'inner'
			? 'break-words font-manrope text-sm font-semibold leading-snug text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]'
			: 'break-words font-manrope text-sm font-semibold leading-snug text-[#595c5e]'

	const resolvedTokenId = tokenId?.trim() || meta.tokenId?.trim() || null
	const resolvedSupply = supplySummary?.trim() || meta.supplySummary?.trim() || null
	const showCouponAddressMeta =
		!isCatalog &&
		!isDiscoverMerchant &&
		(Boolean(resolvedTokenId) ||
			Boolean(meta.shareUrl?.trim()) ||
			Boolean(resolvedSupply) ||
			likeCount != null ||
			shareClickCount != null)
	const addressMetaVariant = tone === 'inner' ? 'onDark' : 'light'

	if (isCatalog) {
		const global = meta.globalCategory?.trim() ?? ''
		const item = meta.itemCategory?.trim() ?? ''
		const isVideoOg = meta.catalogLayout === 'videoOg'
		const publisherLine = meta.publisherLine?.trim() ?? ''
		const publisherClass =
			tone === 'inner'
				? 'truncate text-xs font-medium text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]'
				: 'truncate text-xs font-medium text-[#595c5e]'
		if (isVideoOg) {
			const descriptionMarginClass = publisherLine || title ? 'mt-0.5' : global || item ? 'mt-1' : ''
			return (
				<div
					className={`min-w-0 ${descriptionViewportScroll ? 'flex min-h-0 flex-1 flex-col' : 'flex-1'}`}
				>
					<div className="shrink-0">
						<CatalogShareCategoryLine
							globalCategory={meta.globalCategory}
							itemCategory={meta.itemCategory}
							tone={tone}
						/>
						{title ? <p className={`${titleClass} ${global || item ? 'mt-1' : ''}`}>{title}</p> : null}
						{publisherLine ? (
							<p className={`${publisherClass} ${title ? 'mt-0.5' : global || item ? 'mt-1' : ''}`}>
								{publisherLine}
							</p>
						) : null}
					</div>
					{subtitle ? (
						descriptionViewportScroll ? (
							<div
								className={`catalog-share-description-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain ${descriptionMarginClass}`}
							>
								<p className={subtitleClass}>{subtitle}</p>
							</div>
						) : (
							<p className={`${subtitleClass} ${descriptionMarginClass}`}>{subtitle}</p>
						)
					) : null}
					{showExpiryPill ? (
						<div
							className={`shrink-0 ${title || subtitle || publisherLine || global || item ? 'mt-2' : ''}`}
						>
							{renderExpiryPill(tone)}
						</div>
					) : null}
				</div>
			)
		}

		return (
			<div className="font-manrope min-w-0 flex-1">
				<CatalogShareCategoryLine
					globalCategory={meta.globalCategory}
					itemCategory={meta.itemCategory}
					tone={tone}
				/>
				{title ? <p className={`${titleClass} ${global || item ? 'mt-1' : ''}`}>{title}</p> : null}
				{subtitle ? <p className={`${subtitleClass} ${title ? 'mt-0.5' : global || item ? 'mt-1' : ''}`}>{subtitle}</p> : null}
				{showExpiryPill ? (
					<div className={title || subtitle || global || item ? 'mt-2' : ''}>{renderExpiryPill(tone)}</div>
				) : null}
			</div>
		)
	}

	if (isDiscoverMerchant && meta.catalogLayout === 'videoOg') {
		const descriptionMarginClass = title ? 'mt-0.5' : 'mt-1'
		return (
			<div
				className={`min-w-0 ${descriptionViewportScroll ? 'flex min-h-0 flex-1 flex-col' : 'flex-1'}`}
			>
				<div className="shrink-0">
					<p className="text-[10px] font-black uppercase tracking-wide text-[#1562f0]">Discover</p>
					{title ? <p className={`${titleClass} mt-1`}>{title}</p> : null}
				</div>
				{subtitle ? (
					descriptionViewportScroll ? (
						<div
							className={`catalog-share-description-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain ${descriptionMarginClass}`}
						>
							<p className={subtitleClass}>{subtitle}</p>
						</div>
					) : (
						<p className={`${subtitleClass} ${descriptionMarginClass}`}>{subtitle}</p>
					)
				) : null}
				{showExpiryPill ? (
					<div className={title || subtitle ? 'mt-2' : ''}>{renderExpiryPill(tone)}</div>
				) : null}
			</div>
		)
	}

	return (
		<div className={`font-manrope min-w-0 flex-1 ${tone === 'inner' ? 'text-white' : ''}`}>
			{title ? (
				<p className={`truncate text-[1.05rem] font-extrabold leading-tight tracking-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] sm:text-lg ${tone === 'external' ? 'text-[#2c2f31]' : ''}`}>
					{title}
				</p>
			) : null}
			{subtitle ? (
				<p
					className={`truncate text-sm font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)] ${
						title ? 'mt-0.5' : ''
					} ${tone === 'external' ? 'text-[#595c5e]' : 'text-white/90'}`}
				>
					{subtitle}
				</p>
			) : null}
			{showCouponAddressMeta ? (
				<CouponTicketAddressMetaRow
					cardAddress={meta.cardAddress}
					tokenId={resolvedTokenId}
					shareUrl={meta.shareUrl}
					shareTitle={meta.title}
					supplySummary={resolvedSupply}
					likeCount={likeCount}
					shareClickCount={shareClickCount}
					variant={addressMetaVariant}
					className={title || subtitle ? 'mt-1.5' : 'mt-0.5'}
				/>
			) : null}
			{showExpiryPill ? (
				<div className={title || subtitle || showCouponAddressMeta ? 'mt-2' : ''}>
					{renderExpiryPill(tone)}
				</div>
			) : null}
		</div>
	)
}

function isDiscoverMerchantMeta(meta: CouponClaimShareMeta): boolean {
	if (meta.shareKind === 'personal_invite') return false
	return meta.shareKind === 'discover_merchant' || meta.distributionKind === 'merchant'
}

function CouponSharePreview({
	meta,
	referrerEoa = null,
	enableTempWalletClaim = false,
	onPayReadyChange,
}: {
	meta: CouponClaimShareMeta
	referrerEoa?: string | null
	/** Open-claim: Gift icon on ticket right uses visit/temp wallet. */
	enableTempWalletClaim?: boolean
	/** True when visit wallet holds this coupon (claimed, not burned) — parent shows Show Pay for POS. */
	onPayReadyChange?: (ready: boolean) => void
}) {
	const catalogVideoOgCardRef = React.useRef<HTMLDivElement>(null)
	const catalogVideoOgCardMaxHeightPx = useCatalogVideoOgShareCardMaxHeight(catalogVideoOgCardRef)
	const expiryUrgent = couponExpiryUsesUrgentVariant(meta.expiresLabel)
	const showExpiryPill = shouldShowCouponExpiryPill(meta.expiresLabel)
	const ExpiryIcon = expiryUrgent ? Clock : Calendar
	const hasBanner = Boolean(meta.backgroundImage?.trim())
	const isCatalogVideoOg = meta.distributionKind === 'catalog' && meta.catalogLayout === 'videoOg'
	const isCouponTicket =
		meta.distributionKind !== 'catalog' &&
		meta.shareKind !== 'discover_merchant' &&
		meta.distributionKind !== 'merchant'
	const showTempClaim =
		enableTempWalletClaim &&
		isCouponTicket &&
		meta.shareKind !== 'redeem' &&
		Boolean(meta.cardAddress?.trim() && meta.couponId?.trim())

	const [seriesExtras, setSeriesExtras] = useState<{
		tokenId: string | null
		supplySummary: string | null
		seriesItem: CardActiveIssuedCouponSeriesItem | null
	}>({
		tokenId: meta.tokenId ?? null,
		supplySummary: meta.supplySummary ?? null,
		seriesItem: null,
	})
	const [socialStats, setSocialStats] = useState<CardProgramSocialSummary | null>(null)
	const [claimStatus, setClaimStatus] = useState<CouponTempClaimStatus>('idle')
	const [claimError, setClaimError] = useState<string | undefined>()
	const [eligibility, setEligibility] = useState<CouponOpenClaimEligibility | null>(null)
	const { visitAaReady, walletBlobRef, requestAaRetry } = useVisitWalletAaReady(showTempClaim)
	const claimStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		if (!isCouponTicket) return
		const card = meta.cardAddress?.trim() ?? ''
		const couponId = meta.couponId?.trim() ?? ''
		if (!card || !couponId) return
		let cancelled = false
		void fetchCouponShareSeriesExtras(card, couponId).then((extras) => {
			if (cancelled) return
			setSeriesExtras((prev) => ({
				tokenId: extras.tokenId || prev.tokenId,
				supplySummary: extras.supplySummary || prev.supplySummary,
				seriesItem: extras.seriesItem ?? prev.seriesItem,
			}))
		})
		return () => {
			cancelled = true
		}
	}, [isCouponTicket, meta.cardAddress, meta.couponId])

	useEffect(() => {
		if (!isCouponTicket) return
		const card = meta.cardAddress?.trim() ?? ''
		const tokenId = (seriesExtras.tokenId || meta.tokenId || '').trim()
		if (!card || !tokenId) return
		let cancelled = false
		void fetchCouponProgramSocialSummary(card, tokenId).then((summary) => {
			if (cancelled || !summary) return
			setSocialStats(summary)
		})
		return () => {
			cancelled = true
		}
	}, [isCouponTicket, meta.cardAddress, meta.tokenId, seriesExtras.tokenId])

	// Temp-wallet claimed / redeemed status (local-first visit wallet → chain).
	useEffect(() => {
		if (!showTempClaim || !seriesExtras.seriesItem || !visitAaReady) {
			if (!showTempClaim || !seriesExtras.seriesItem) setEligibility(null)
			return
		}
		let cancelled = false
		void (async () => {
			let blob = walletBlobRef.current
			if (!blob) {
				blob = await provisionWebShareVisitWallet()
				walletBlobRef.current = blob
			}
			const wallet = resolveSigningWalletFromBlob(blob)
			const userEoa = wallet?.address ?? null
			const next = await resolveCouponOpenClaimEligibilityForItem(seriesExtras.seriesItem!, userEoa)
			if (!cancelled) setEligibility(next)
		})()
		return () => {
			cancelled = true
		}
	}, [showTempClaim, seriesExtras.seriesItem, visitAaReady])

	useEffect(
		() => () => {
			if (claimStatusTimerRef.current) clearTimeout(claimStatusTimerRef.current)
		},
		[],
	)

	const scheduleClaimErrorReset = useCallback(() => {
		if (claimStatusTimerRef.current) clearTimeout(claimStatusTimerRef.current)
		claimStatusTimerRef.current = setTimeout(() => {
			setClaimStatus((s) => (s === 'error' ? 'idle' : s))
			setClaimError(undefined)
			claimStatusTimerRef.current = null
		}, 3000)
	}, [])

	const isAlreadyClaimed = eligibility === 'already_claimed'
	const isAlreadyRedeemed = eligibility === 'already_redeemed'
	const claimBlocked =
		isAlreadyClaimed ||
		isAlreadyRedeemed ||
		eligibility === 'sold_out' ||
		eligibility === 'expired' ||
		eligibility === 'not_open_claim' ||
		eligibility === 'insufficient_social_points'

	const payReady = showTempClaim && isAlreadyClaimed && !isAlreadyRedeemed
	useEffect(() => {
		onPayReadyChange?.(payReady)
		return () => onPayReadyChange?.(false)
	}, [onPayReadyChange, payReady])

	const handleTempWalletClaim = useCallback(async () => {
		if (!showTempClaim || !visitAaReady || claimStatus !== 'idle' || claimBlocked) return

		setClaimStatus('loading')
		setClaimError(undefined)
		try {
			const prepared = await prepareVisitWalletForOpenClaim(walletBlobRef.current)
			if (!prepared) {
				// AA not ready yet — resume spinner gate; Claim auto-enables when ready.
				requestAaRetry()
				setClaimStatus('idle')
				return
			}
			walletBlobRef.current = prepared.blob

			const ret = await postCardCouponOpenClaimWithWallet({
				cardAddress: meta.cardAddress,
				couponId: meta.couponId,
				tokenId: seriesExtras.tokenId ?? meta.tokenId ?? undefined,
				privateKeyArmor: prepared.privateKeyArmor,
				referrerEoa,
			})
			if (ret.success) {
				setEligibility('already_claimed')
				setClaimStatus('idle')
			} else {
				const err = ret.error ?? 'Coupon claim failed'
				if (/already claimed/i.test(err)) {
					setEligibility('already_claimed')
					setClaimStatus('idle')
				} else if (/already used|already redeemed|burned/i.test(err)) {
					setEligibility('already_redeemed')
					setClaimStatus('idle')
				} else {
					setClaimStatus('error')
					setClaimError(err)
					scheduleClaimErrorReset()
				}
			}
		} catch (e: unknown) {
			setClaimStatus('error')
			setClaimError(e instanceof Error ? e.message : 'Coupon claim failed')
			scheduleClaimErrorReset()
		}
	}, [
		claimBlocked,
		claimStatus,
		meta.cardAddress,
		meta.couponId,
		meta.tokenId,
		referrerEoa,
		requestAaRetry,
		scheduleClaimErrorReset,
		seriesExtras.tokenId,
		showTempClaim,
		visitAaReady,
	])

	const iconUrl = isCatalogVideoOg
		? meta.iconUrl.trim()
		: hasBanner
			? ''
			: meta.iconUrl.trim()
	const innerExpiryClass = expiryUrgent
		? 'bg-red-600 text-white shadow-sm shadow-red-900/25'
		: 'border border-white/25 bg-slate-950/65 text-white shadow-sm shadow-black/20 backdrop-blur-md'
	const externalExpiryClass = expiryUrgent
		? 'bg-red-600 text-white shadow-sm shadow-red-900/25'
		: 'border border-[#abadaf]/35 bg-[#eef1f3] text-[#595c5e]'

	const shareHeadline =
		meta.distributionKind === 'catalog'
			? ''
			: meta.shareHeadline?.trim() ||
				(meta.merchantName?.trim()
					? `${meta.shareKind === 'redeem' ? 'Redeem' : 'Claim'} a ${meta.merchantName.trim()} Coupon`
					: '')

	const renderExpiryPill = (placement: 'inner' | 'external') => (
		<div
			className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${
				placement === 'external' ? externalExpiryClass : innerExpiryClass
			}`}
		>
			<ExpiryIcon className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
			<span className="truncate">{meta.expiresLabel}</span>
		</div>
	)

	const metadataProps = {
		tokenId: seriesExtras.tokenId,
		supplySummary: seriesExtras.supplySummary,
		likeCount: socialStats?.likeCount ?? null,
		shareClickCount: socialStats?.shareClickCount ?? null,
	} as const

	const claimBusy = !visitAaReady || claimStatus === 'loading'
	const claimActionAriaLabel = isAlreadyRedeemed
		? 'Coupon already redeemed'
		: isAlreadyClaimed
			? 'Coupon claimed'
			: !visitAaReady
				? 'Preparing Smart Wallet'
				: claimStatus === 'error'
					? claimError ?? 'Coupon claim failed'
					: 'Claim with temporary wallet'

	const claimButton = showTempClaim ? (
		<div className="pointer-events-auto absolute right-6 top-1/2 z-[2] -translate-y-1/2 sm:right-8">
			{isAlreadyRedeemed ? (
				<span
					className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-400/50 bg-slate-100/90 shadow-sm ring-1 ring-slate-300/40 backdrop-blur-sm"
					aria-label={claimActionAriaLabel}
					title="Redeemed"
				>
					<Ticket className="h-4 w-4 text-slate-500" strokeWidth={2.25} aria-hidden />
				</span>
			) : isAlreadyClaimed ? (
				<span
					className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-500/40 bg-transparent shadow-sm ring-1 ring-emerald-500/15 backdrop-blur-sm"
					aria-label={claimActionAriaLabel}
					title="Claimed"
				>
					<Check className="h-4 w-4 text-emerald-500" strokeWidth={2.4} aria-hidden />
				</span>
			) : (
				<button
					type="button"
					disabled={!visitAaReady || claimStatus !== 'idle' || claimBlocked}
					onClick={(e) => {
						e.stopPropagation()
						void handleTempWalletClaim()
					}}
					className="inline-flex items-center justify-center rounded-full px-2.5 py-1.5 transition-opacity active:scale-95 disabled:cursor-not-allowed disabled:opacity-55"
					style={{ background: CLAIM_GRADIENT }}
					title={
						!visitAaReady
							? 'Preparing Smart Wallet…'
							: claimStatus === 'error'
								? claimError
								: 'Claim with temporary wallet'
					}
					aria-label={claimActionAriaLabel}
					aria-busy={claimBusy}
				>
					{claimBusy ? (
						<Loader2 className="h-4 w-4 animate-spin text-white" aria-hidden />
					) : claimStatus === 'error' ? (
						<AlertTriangle className="h-4 w-4 text-white" strokeWidth={2.4} aria-hidden />
					) : (
						<Gift className="h-4 w-4 text-white" strokeWidth={2} aria-hidden />
					)}
				</button>
			)}
		</div>
	) : null

	if (isCatalogVideoOg && hasBanner) {
		return (
			<div ref={catalogVideoOgCardRef} className="mx-auto w-full min-w-0 max-w-lg text-left">
				<div
					className="flex flex-col overflow-hidden rounded-[1.75rem] ring-1 ring-black/[0.08]"
					style={catalogVideoOgCardMaxHeightPx ? { maxHeight: catalogVideoOgCardMaxHeightPx } : undefined}
				>
					<div className="shrink-0">
						<CatalogVideoOgBannerMedia
							bannerImageUrl={meta.backgroundImage}
							productionVideoUrl={meta.productionVideoUrl}
							productionVideoMime={meta.productionVideoMime}
							iconUrl={meta.iconUrl}
							backgroundColorHex={meta.backgroundColorHex}
							previewBannerHeightPx={CATALOG_VIDEO_OG_APP_DOWNLOAD_BANNER_HEIGHT_PX}
							interactivePlayback
						/>
					</div>
					<div
						className={`${CATALOG_VIDEO_OG_BELOW_BANNER_ROW_OG_PREVIEW_CLASSNAME} flex min-h-0 flex-1 flex-col overflow-hidden`}
					>
						<CatalogShareMetadataBlock
							meta={meta}
							tone="external"
							showExpiryPill={showExpiryPill}
							renderExpiryPill={renderExpiryPill}
							descriptionViewportScroll
						/>
					</div>
				</div>
			</div>
		)
	}

	const ticketPunchInsetClass = 'px-[18px]'

	const ticketShell = (
		<div className={`relative w-full min-w-0 ${ticketPunchInsetClass}`}>
			<div className="relative w-full min-w-0 rounded-[1.75rem]">
				<div
					className="pointer-events-none absolute left-0 top-1/2 z-20 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#f8fafc]"
					aria-hidden
				/>
				<div
					className="pointer-events-none absolute right-0 top-1/2 z-20 h-9 w-9 translate-x-1/2 -translate-y-1/2 rounded-full bg-[#f8fafc]"
					aria-hidden
				/>
				<div className="relative min-h-[7.5rem] overflow-hidden rounded-[1.75rem] ring-1 ring-black/[0.08]">
				{hasBanner ? (
					<CouponBannerImage src={meta.backgroundImage} />
				) : (
					<>
						<div
							className="absolute inset-0"
							style={{ backgroundColor: meta.backgroundColorHex || '#2B2E3A' }}
						/>
						<div
							className="pointer-events-none absolute inset-0 opacity-[0.12]"
							style={{
								backgroundImage:
									'repeating-linear-gradient(-26deg, #fff 0, #fff 1px, transparent 1px, transparent 8px)',
							}}
							aria-hidden
						/>
						<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/30" aria-hidden />
					</>
				)}

				{!hasBanner ? (
					<div
						className={[
							'relative z-[1] flex min-h-[7.5rem] min-w-0 items-center gap-3 px-5 py-4 sm:gap-4 sm:px-7 sm:py-5',
							showTempClaim ? 'pr-[6.25rem] sm:pr-[6.75rem]' : '',
						].join(' ')}
					>
						{iconUrl ? (
							<div className="relative flex h-[3.35rem] w-[3.35rem] shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/40 bg-white/95 shadow-md ring-2 ring-black/10 sm:h-14 sm:w-14">
								<img src={iconUrl} alt="" className="h-full w-full object-cover" draggable={false} />
							</div>
						) : null}

						<CatalogShareMetadataBlock
							meta={meta}
							tone="inner"
							showExpiryPill={showExpiryPill}
							renderExpiryPill={renderExpiryPill}
							{...metadataProps}
						/>
						{claimButton}
					</div>
				) : showTempClaim ? (
					<div className="relative z-[1] flex min-h-[7.5rem] items-center pr-[6.25rem] sm:pr-[6.75rem]">
						{claimButton}
					</div>
				) : null}
				</div>
			</div>
		</div>
	)

	return (
		<div className="mx-auto w-full min-w-0 max-w-full text-left">
			{shareHeadline ? (
				<p className="mb-3 text-center font-manrope text-base font-extrabold tracking-tight text-slate-900 sm:text-lg">
					{shareHeadline}
				</p>
			) : null}
			{ticketShell}
			{hasBanner ? (
				<div className="mt-3 w-full min-w-0">
					<CatalogShareMetadataBlock
						meta={meta}
						tone="external"
						showExpiryPill={showExpiryPill}
						renderExpiryPill={renderExpiryPill}
						{...metadataProps}
					/>
				</div>
			) : null}
			{claimStatus === 'error' && claimError ? (
				<p className="mt-2 px-1 text-center text-[11px] font-semibold text-amber-600">{claimError}</p>
			) : null}
			{isAlreadyClaimed ? (
				<p className="mt-2 px-1 text-center text-[12px] font-medium text-emerald-600">
					You already claimed this coupon. Show Pay at the merchant POS to redeem.
				</p>
			) : null}
			{isAlreadyRedeemed ? (
				<p className="mt-2 px-1 text-center text-[12px] font-medium text-slate-500">
					This temporary wallet already used this coupon.
				</p>
			) : null}
			{eligibility === 'insufficient_social_points' ? (
				<p className="mt-2 px-1 text-center text-[11px] font-semibold text-amber-600">
					Not enough social points for this exchange.
				</p>
			) : null}
			{eligibility === 'sold_out' ? (
				<p className="mt-2 px-1 text-center text-[11px] font-semibold text-amber-600">
					Coupon supply has been fully claimed.
				</p>
			) : null}
			{eligibility === 'expired' ? (
				<p className="mt-2 px-1 text-center text-[11px] font-semibold text-amber-600">
					This coupon is inactive or expired.
				</p>
			) : null}
		</div>
	)
}

function CouponShareClaimActions({ onShowPay }: { onShowPay?: () => void }) {
	return (
		<div className="mx-auto mt-6 w-full max-w-xs sm:max-w-sm">
			<button
				type="button"
				onClick={() => onShowPay?.()}
				aria-label="Show Pay"
				className="inline-flex w-full items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-opacity hover:opacity-95 active:scale-[0.98] sm:py-4 sm:text-[15px]"
				style={{ background: CLAIM_GRADIENT }}
			>
				<QrCode className="h-5 w-5 shrink-0" strokeWidth={2.25} aria-hidden />
				<span>Show Pay</span>
			</button>
		</div>
	)
}

export default function AppDownloadPage() {
	const location = useLocation()
	const targetUrl = useMemo(() => resolveBeamioAppTarget(location.search), [location.search])
	const shareUrl = useMemo(() => buildAppDownloadShareUrl(location.search), [location.search])
	/** iOS / Telegram / in-app: skip silent `beamio://` probe → install. Android may silent-probe. */
	const [phase, setPhase] = useState<PagePhase>(() => {
		if (!isMobileDevice()) return 'desktop'
		if (shouldSkipSilentNativeAppProbe()) return 'install'
		return 'checking'
	})
	const [shareMeta, setShareMeta] = useState<CouponClaimShareMeta | null>(null)
	const [discoverSocialStats, setDiscoverSocialStats] = useState<CardProgramSocialSummary | null>(null)
	const [visitWalletProfile, setVisitWalletProfile] = useState<AppDownloadVisitWalletProfile | null>(null)
	const [myWalletOpen, setMyWalletOpen] = useState(false)
	const [payCodeOpen, setPayCodeOpen] = useState(false)
	const [couponPayReady, setCouponPayReady] = useState(false)
	const [openInAppBusy, setOpenInAppBusy] = useState(false)
	/** Android silent probe detected Consumer installed (result === 'opened'). */
	const [consumerAppLikelyInstalled, setConsumerAppLikelyInstalled] = useState(false)
	const redirectingToInnerTarget = Boolean(targetUrl && shouldRedirectToInnerAppTarget())
	const shareClickStartedRef = useRef(false)
	const { opacity: capsuleOpacity } = useScrollCapsuleOpacity(true, 'window')

	/**
	 * Open in App: Android Intent pins Consumer (`com.beamio.app`).
	 * iOS never probes `beamio://` (POS historically claimed that scheme).
	 * Installed Consumer is opened via Universal Links; otherwise App Store.
	 */
	const handleOpenInApp = useCallback(() => {
		if (openInAppBusy) return
		setOpenInAppBusy(true)
		void (async () => {
			try {
				await openBeamioAppOrStore(location.search, {
					deepLinkUrl: targetUrl,
					pageSearch: location.search,
					probeTimeoutMs: 1800,
				})
			} finally {
				window.setTimeout(() => setOpenInAppBusy(false), 400)
			}
		})()
	}, [location.search, openInAppBusy, targetUrl])

	/**
	 * The merchant landing CTA is a web-PWA entry point, not a native-app
	 * probe. Reuse the validated /app target so beamiocard, discover and ref
	 * parameters remain intact and correctly encoded.
	 */
	const handleOpenInPwa = useCallback(() => {
		// Carry EVERY deep-link param into the PWA: params inside `target` plus any
		// outer params on this page (e.g. beamiocard / discover / ref / couponId
		// sitting next to, or instead of, `target`). Inner params win on conflict.
		const url = new URL(targetUrl || 'https://beamio.app/app/')
		new URLSearchParams(location.search).forEach((value, key) => {
			if (key === 'target') return
			if (!url.searchParams.has(key)) url.searchParams.set(key, value)
		})
		window.location.assign(url.toString())
	}, [targetUrl, location.search])

	useLayoutEffect(() => {
		if (!shareClickStartedRef.current) {
			const couponOpen = parseCouponOpenClaimFromTarget(targetUrl)
			const openFromTarget = parseDiscoverMerchantOpenFromTarget(targetUrl)
			const cardFromTarget =
				couponOpen?.cardAddress ??
				openFromTarget?.cardAddress ??
				parseDiscoverMerchantCardFromTarget(targetUrl)
			// Discover merchant join landing stays read-only: recording a share click
			// needs a visit wallet (temp wallet + signature), which we do not create here.
			if (cardFromTarget && couponOpen) {
				shareClickStartedRef.current = true
				void recordDiscoverShareClickIfNeeded(cardFromTarget, {
					referrerEoa: couponOpen?.referrerEoa ?? openFromTarget?.referrerEoa ?? null,
					...(couponOpen ? { couponId: couponOpen.couponId } : {}),
				}).then((result) => {
					if (!result.ok) return
					void fetchCardProgramSocialSummary(cardFromTarget).then((summary) => {
						if (summary) setDiscoverSocialStats(summary)
					})
				})
			}
		}
		// Stash early (landing view) so install from store badges still carries referrer.
		if (targetUrl) {
			stashPendingConsumerDeepLink(targetUrl)
		}
		if (!targetUrl || !shouldRedirectToInnerAppTarget()) return
		window.location.replace(targetUrl)
	}, [targetUrl])

	/** Viewport scroll + page-scoped layout (class on body avoids html MutationObserver churn). */
	useEffect(() => {
		document.documentElement.classList.add('beamio-app-download-page')
		document.body.classList.add('beamio-app-download-page')
		return () => {
			document.documentElement.classList.remove('beamio-app-download-page')
			document.body.classList.remove('beamio-app-download-page')
		}
	}, [])

	/** iOS "Open in Beamio" system dimmer only covers the layout viewport — freeze scroll/height while probing. */
	useEffect(() => {
		if (phase !== 'checking') {
			document.documentElement.classList.remove('beamio-app-download-page-checking')
			document.body.classList.remove('beamio-app-download-page-checking')
			return
		}
		document.documentElement.classList.add('beamio-app-download-page-checking')
		document.body.classList.add('beamio-app-download-page-checking')
		window.scrollTo(0, 0)
		return () => {
			document.documentElement.classList.remove('beamio-app-download-page-checking')
			document.body.classList.remove('beamio-app-download-page-checking')
		}
	}, [phase])

	useEffect(() => {
		if (redirectingToInnerTarget) return
		if (!targetUrl || !shareUrl) {
			document.title = 'Get Beamio App'
			return
		}

		let cancelled = false
		void (async () => {
			const meta = await fetchCouponClaimShareMeta(shareUrl)
			if (cancelled) return
			if (meta) {
				setShareMeta(meta)
				applyCouponClaimShareMeta(meta)
			} else {
				document.title = 'Get Beamio App'
			}
		})()

		return () => {
			cancelled = true
		}
	}, [redirectingToInnerTarget, shareUrl, targetUrl])

	const discoverMerchantCardAddress = useMemo(() => {
		if (shareMeta && isDiscoverMerchantMeta(shareMeta) && shareMeta.cardAddress) {
			try {
				return ethers.getAddress(shareMeta.cardAddress)
			} catch {
				return null
			}
		}
		return parseDiscoverMerchantCardFromTarget(targetUrl)
	}, [shareMeta, targetUrl])

	const discoverReferrerEoa = useMemo(
		() => parseDiscoverMerchantOpenFromTarget(targetUrl)?.referrerEoa ?? null,
		[targetUrl],
	)

	const isDiscoverMerchantShare = Boolean(
		discoverMerchantCardAddress &&
			((shareMeta && isDiscoverMerchantMeta(shareMeta)) ||
				Boolean(parseDiscoverMerchantOpenFromTarget(targetUrl)?.cardAddress)),
	)

	/** Coupon / redeem open-claim card data used by the Discover actions. */
	const couponShareCardAddress = useMemo(() => {
		const fromMeta = shareMeta?.cardAddress?.trim() ?? ''
		if (fromMeta && ethers.isAddress(fromMeta) && shareMeta && isCouponShareMeta(shareMeta) && !isDiscoverMerchantMeta(shareMeta)) {
			try {
				return ethers.getAddress(fromMeta)
			} catch {
				/* fall through */
			}
		}
		const fromTarget = parseCouponOpenClaimFromTarget(targetUrl)?.cardAddress
		return fromTarget ?? null
	}, [shareMeta, targetUrl])

	const topBarCardAddress = discoverMerchantCardAddress ?? couponShareCardAddress
	const showAppDownloadTopBar = Boolean(topBarCardAddress) && !myWalletOpen

	const effectiveShareMeta = useMemo((): CouponClaimShareMeta | null => {
		if (shareMeta) return shareMeta
		if (!discoverMerchantCardAddress || !shareUrl) return null
		return {
			cardAddress: discoverMerchantCardAddress,
			couponId: '',
			shareKind: 'discover_merchant',
			distributionKind: 'merchant',
			catalogLayout: 'videoOg',
			title: 'Merchant',
			subtitle: '',
			iconUrl: '',
			backgroundImage: '',
			backgroundColorHex: '#2B2E3A',
			validBeforeSec: null,
			expiresLabel: 'VALID NOW',
			shareUrl,
			ogImageUrl: '',
		}
	}, [discoverMerchantCardAddress, shareMeta, shareUrl])

	useEffect(() => {
		// Merchant join landing does not show social stats; skip the chain read.
		const card = isDiscoverMerchantShare ? null : couponShareCardAddress
		if (!card) {
			setDiscoverSocialStats(null)
			return
		}
		let cancelled = false
		void (async () => {
			const summary = await fetchCardProgramSocialSummary(card)
			if (!cancelled && summary) setDiscoverSocialStats(summary)
		})()
		return () => {
			cancelled = true
		}
	}, [couponShareCardAddress, isDiscoverMerchantShare])

	/** Visit / temp wallet for Discover top bar + coupon Show Pay QR (POS 核销). */
	useEffect(() => {
		if (redirectingToInnerTarget) return
		// Merchant join landing never detects / provisions a visit wallet.
		const needsVisitWallet =
			!isDiscoverMerchantShare &&
			Boolean(parseCouponOpenClaimFromTarget(targetUrl)?.couponId)
		if (!needsVisitWallet) return
		let cancelled = false
		void (async () => {
			const profile = await loadAppDownloadVisitWalletProfile()
			if (!cancelled && profile) setVisitWalletProfile(profile)
		})()
		return () => {
			cancelled = true
		}
	}, [isDiscoverMerchantShare, redirectingToInnerTarget, targetUrl])

	useEffect(() => {
		if (!myWalletOpen) return
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = prev
		}
	}, [myWalletOpen])

	/**
	 * Merchant / coupon / invitation landings (Android):
	 * 0) Intent miss redirect (`?beamioNativeMiss=`) → store (definitive not installed).
	 * 1) Digital Asset Links + `getInstalledRelatedApps` / localStorage latch.
	 * 2) Else silent Intent + same-origin miss URL (no Play jump).
	 * iOS / in-app browsers skip silent probe → store badges.
	 */
	useLayoutEffect(() => {
		if (redirectingToInnerTarget) return

		let cancelled = false

		void (async () => {
			if (!isMobileDevice()) {
				setConsumerAppLikelyInstalled(false)
				setPhase('desktop')
				return
			}

			if (isIosDevice() && !targetUrl) {
				setConsumerAppLikelyInstalled(false)
				window.location.replace(BEAMIO_IOS_STORE_URL)
				return
			}

			/** Intent miss fallback landed here — Consumer not installed. */
			if (isAndroidDevice() && consumeAndroidSilentProbeMiss(location.search)) {
				setConsumerAppLikelyInstalled(false)
				setPhase('install')
				return
			}

			/** iOS / Telegram / in-app: skip silent probe (iOS cannot pin Consumer). */
			if (shouldSkipSilentNativeAppProbe()) {
				setConsumerAppLikelyInstalled(false)
				setPhase('install')
				return
			}

			setPhase('checking')
			window.scrollTo(0, 0)

			/**
			 * Invitation CTA needs install detection without a share target.
			 * Sideloaded Consumer APKs often get relatedApps `[]` — do not skip Intent when
			 * latch is empty; probe even without `?target=`.
			 */
			const relatedOrHint = await resolveAndroidConsumerAppLikelyInstalled()
			if (cancelled) return
			if (relatedOrHint) {
				setConsumerAppLikelyInstalled(true)
				setPhase('install')
				return
			}

			/**
			 * Silent probe: iframe `beamio://` then Intent + same-origin miss URL.
			 * installed → finish('opened') latches immediately (survives frozen tab);
			 * missing → reload with beamioNativeMiss (handled above; clears latch).
			 */
			const { failUrl } = buildAndroidSilentProbeMissUrl(window.location.href)
			const result = await attemptOpenNativeBeamioApp(location.search, {
				useLocationNavigation: true,
				timeoutMs: 3200,
				storeFallback: false,
				browserFallbackUrl: failUrl,
			})
			if (cancelled) return
			if (result === 'opened') {
				markConsumerAppInstalledHint()
				setConsumerAppLikelyInstalled(true)
				setPhase('install')
				return
			}
			setConsumerAppLikelyInstalled(false)
			setPhase('install')
		})()

		return () => {
			cancelled = true
		}
	}, [location.search, redirectingToInnerTarget, targetUrl])

	/**
	 * After silent Intent opens the app, Chrome often freezes the tab before React
	 * paints `open_app`. On return, re-read latch / relatedApps so CTA flips.
	 */
	useEffect(() => {
		if (redirectingToInnerTarget || !isAndroidDevice()) return
		const refreshInstalledHint = () => {
			if (document.visibilityState !== 'visible') return
			void resolveAndroidConsumerAppLikelyInstalled().then((ok) => {
				if (ok) {
					setConsumerAppLikelyInstalled(true)
					setPhase((p) => (p === 'checking' || p === 'install' ? 'install' : p))
				}
			})
		}
		document.addEventListener('visibilitychange', refreshInstalledHint)
		window.addEventListener('focus', refreshInstalledHint)
		return () => {
			document.removeEventListener('visibilitychange', refreshInstalledHint)
			window.removeEventListener('focus', refreshInstalledHint)
		}
	}, [redirectingToInnerTarget])

	const storeUrl = isIosDevice()
		? BEAMIO_IOS_STORE_URL
		: targetUrl
			? buildPlayStoreUrlWithDeepLinkReferrer(targetUrl)
			: BEAMIO_ANDROID_STORE_URL

	const onStoreBadgeClick = useCallback(() => {
		if (targetUrl) stashPendingConsumerDeepLink(targetUrl)
	}, [targetUrl])

	const invitationCtaMode: InvitationCtaMode = useMemo(() => {
		if (!isMobileDevice()) return 'enter_pwa'
		if (consumerAppLikelyInstalled) return 'open_app'
		return 'store'
	}, [consumerAppLikelyInstalled])

	const inviteEoa = useMemo(() => {
		const fromTarget = parseInviteEoaFromAppTarget(targetUrl)
		if (fromTarget) return fromTarget
		/** Also allow `?invite=0x…` on the app-download URL itself (not only nested target). */
		try {
			const raw =
				new URLSearchParams(location.search).get('invite')?.trim() ||
				new URLSearchParams(location.search).get('Invite')?.trim() ||
				''
			if (raw && /^0x[0-9a-fA-F]{40}$/.test(raw)) {
				return ethers.getAddress(raw)
			}
		} catch {
			/* ignore */
		}
		const fromMeta = shareMeta?.introducerEoa?.trim() || ''
		if (fromMeta && ethers.isAddress(fromMeta)) {
			try {
				return ethers.getAddress(fromMeta)
			} catch {
				return fromMeta
			}
		}
		return null
	}, [targetUrl, shareMeta, location.search])

	/**
	 * Show `#beamio-invitation` when:
	 * - invite EOA / personal_invite meta, or
	 * - hash `#beamio-invitation`, or
	 * - plain /app-download with no merchant/coupon share (empty target → was a blank #f8fafc shell).
	 */
	const invitationHashActive =
		location.hash === '#beamio-invitation' || location.hash.replace(/^#/, '') === 'beamio-invitation'

	const showPersonalInvitation =
		!isDiscoverMerchantShare &&
		!couponShareCardAddress &&
		(Boolean(inviteEoa) ||
			isPersonalInviteMeta(shareMeta) ||
			isPersonalInviteMeta(effectiveShareMeta) ||
			invitationHashActive ||
			!targetUrl)

	/** Full landing URL for desktop QR — phone scan continues the same invite flow. */
	const invitationQrValue = useMemo(() => {
		if (typeof window === 'undefined') return shareUrl || targetUrl || ''
		try {
			return shareUrl || `${window.location.origin}${window.location.pathname}${location.search}`
		} catch {
			return shareUrl || targetUrl || ''
		}
	}, [shareUrl, targetUrl, location.search])

	const couponOpenClaim = useMemo(() => parseCouponOpenClaimFromTarget(targetUrl), [targetUrl])

	const couponPreview =
		effectiveShareMeta && shareUrl && !isPersonalInviteMeta(effectiveShareMeta) ? (
			isDiscoverMerchantShare && discoverMerchantCardAddress ? (
				<DiscoverMerchantShareDetail
					cardAddress={discoverMerchantCardAddress}
					shareMeta={effectiveShareMeta}
					socialStats={discoverSocialStats}
					userEoa={visitWalletProfile?.eoaAddress ?? null}
					referrerEoa={discoverReferrerEoa}
					onOpenInApp={handleOpenInPwa}
				/>
			) : !isDiscoverMerchantShare ? (
				<CouponSharePreview
					meta={effectiveShareMeta}
					referrerEoa={couponOpenClaim?.referrerEoa ?? null}
					enableTempWalletClaim={
						effectiveShareMeta.shareKind !== 'redeem' &&
						effectiveShareMeta.distributionKind !== 'catalog'
					}
					onPayReadyChange={setCouponPayReady}
				/>
			) : null
		) : null

	/**
	 * Unlock / Open in App is top-right neon pill (merchant + coupon).
	 * Bottom CTA only for claimed coupon → Show Pay QR.
	 */
	const showCouponClaimActions =
		effectiveShareMeta &&
		targetUrl &&
		isCouponShareMeta(effectiveShareMeta) &&
		!isDiscoverMerchantShare &&
		phase !== 'checking' &&
		couponPayReady

	const couponClaimActions = showCouponClaimActions ? (
			<CouponShareClaimActions onShowPay={() => setPayCodeOpen(true)} />
		) : null

	return (
		<div
			className={`font-sans text-slate-900 selection:bg-[#1562f0]/20 antialiased ${
				isDiscoverMerchantShare ? 'bg-[#f5f7f9]' : 'bg-[#f8fafc]'
			} ${
				phase === 'checking' && !showAppDownloadTopBar
					? 'relative h-[100dvh] max-h-[100dvh] overflow-hidden'
					: 'min-h-[100dvh]'
			}`}
		>
			{phase === 'checking' && !showAppDownloadTopBar ? (
				<div
					className="pointer-events-none fixed inset-0 z-[90] bg-[#2c2f31]/35 backdrop-blur-[1px]"
					aria-hidden
				/>
			) : null}
			{showAppDownloadTopBar && topBarCardAddress ? (
				<AppDownloadDiscoverTopBar
					profile={visitWalletProfile}
					cardAddress={topBarCardAddress}
					merchantTitle={
						effectiveShareMeta?.title ||
						effectiveShareMeta?.merchantName ||
						(isDiscoverMerchantShare ? 'Merchant' : 'Coupon')
					}
					referrerEoa={
						discoverReferrerEoa ??
						parseCouponOpenClaimFromTarget(targetUrl)?.referrerEoa ??
						null
					}
					opacity={capsuleOpacity}
					socialStats={discoverSocialStats}
					onOpenWallet={() => setMyWalletOpen(true)}
					onSocialStatsRefresh={() => {
						void fetchCardProgramSocialSummary(topBarCardAddress).then((summary) => {
							if (summary) setDiscoverSocialStats(summary)
						})
					}}
					openInAppAction={
						targetUrl
							? {
									onClick: () => void handleOpenInApp(),
									busy: openInAppBusy,
								}
							: null
					}
				/>
			) : null}
			<AppDownloadPayCodeSheet
				isOpen={payCodeOpen}
				onClose={() => setPayCodeOpen(false)}
				profile={visitWalletProfile}
				onProfileRefresh={setVisitWalletProfile}
			/>
			{myWalletOpen && visitWalletProfile ? (
				<AppDownloadMyWalletPanel
					profile={visitWalletProfile}
					onClose={() => setMyWalletOpen(false)}
					openInAppUrl={targetUrl || undefined}
					onProfileRefresh={setVisitWalletProfile}
				/>
			) : null}
			<main
				className={`relative z-[1] w-full min-w-0 ${
					isDiscoverMerchantShare
						? 'max-w-none px-0 pb-0 pt-0 text-left'
						: showPersonalInvitation
							? 'mx-auto max-w-[1160px] px-4 pb-16 text-left sm:px-6'
							: 'mx-auto max-w-lg px-4 pb-16 text-center sm:px-6'
				}`}
				style={
					!isDiscoverMerchantShare && showAppDownloadTopBar
						? { paddingTop: BEAMIO_FIXED_CAPSULE_SCROLL_TOP_SPACER }
						: !isDiscoverMerchantShare
							? { paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }
							: undefined
				}
			>
				<div
					className={`flex w-full min-w-0 max-w-full flex-col ${
						isDiscoverMerchantShare
							? 'items-stretch justify-start'
							: 'items-center justify-center py-6'
					}`}
				>
				{phase === 'checking' && (
					<div className="w-full min-w-0 max-w-full space-y-6">
						{couponPreview}
						{showAppDownloadTopBar ? (
							<div className="mx-auto max-w-lg px-4 pb-6 text-center">
								<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1562f0]/10">
									<Loader2 className="h-6 w-6 animate-spin text-[#1562f0]" />
								</div>
								<p className="mt-3 text-[14px] font-medium text-slate-600">
									Checking for the Beamio app on your device…
								</p>
							</div>
						) : (
							<>
								<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1562f0]/10">
									<Loader2 className="h-8 w-8 animate-spin text-[#1562f0]" />
								</div>
								<div>
									<h1 className="text-2xl font-bold tracking-tight text-slate-900">
										{shareMeta
											? shareMeta.shareKind === 'discover_merchant' ||
												shareMeta.shareKind === 'personal_invite'
												? 'Opening Beamio'
												: 'Opening Beamio Coupon'
											: 'Opening Beamio'}
									</h1>
									<p className="mt-3 text-slate-600">Checking for the Beamio app on your device…</p>
								</div>
							</>
						)}
					</div>
				)}

				{phase === 'install' && (
					<div
						className={`w-full min-w-0 max-w-full ${
							isDiscoverMerchantShare ? 'space-y-4' : 'space-y-8'
						}`}
					>
						{couponPreview}
						{couponClaimActions ? (
							<div className={isDiscoverMerchantShare ? 'mx-auto max-w-lg px-4 pb-8' : undefined}>
								{couponClaimActions}
							</div>
						) : null}
						{showPersonalInvitation ? (
							<AppDownloadPersonalInvitation
								variant="mobile"
								qrValue={invitationQrValue}
								inviteEoa={inviteEoa}
								ctaMode={invitationCtaMode}
								storeUrl={storeUrl}
								onOpenApp={handleOpenInApp}
								onEnterBeamio={handleOpenInPwa}
								onStoreBadgeClick={onStoreBadgeClick}
							/>
						) : null}
					</div>
				)}

				{phase === 'desktop' && (
					<div
						className={`w-full min-w-0 max-w-full ${
							isDiscoverMerchantShare ? 'space-y-4' : 'space-y-8'
						}`}
					>
						{couponPreview}
						{couponClaimActions ? (
							<div className={isDiscoverMerchantShare ? 'mx-auto max-w-lg px-4 pb-8' : undefined}>
								{couponClaimActions}
							</div>
						) : null}
						{showPersonalInvitation ? (
							<AppDownloadPersonalInvitation
								variant="desktop"
								qrValue={invitationQrValue}
								inviteEoa={inviteEoa}
								ctaMode="enter_pwa"
								storeUrl={storeUrl}
								onOpenApp={handleOpenInApp}
								onEnterBeamio={handleOpenInPwa}
								onStoreBadgeClick={onStoreBadgeClick}
							/>
						) : null}
					</div>
				)}
				</div>
			</main>
		</div>
	)
}
